import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { addDocumentNpwp } from "features/common/layouts/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  sendDocumentNpwp,
  resetStatusNPWP,
} from "features/common/layouts/cartSlice";
import { notifyWarning, notifySuccess } from "components/ToastMessage";

const ModalFakturPajak = (props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [sendFile, setSendFile] = useState("");
  const [formData, setFormData] = useState({
    npwp_no: "",
    npwp_company: "",
    npwp_address: "",
  });

  // Add validation error states
  const [errors, setErrors] = useState({
    npwp_no: "",
    npwp_company: "",
    npwp_address: "",
  });

  const { status_login } = useSelector((state) => state.auth);
  const { documentNpwp } = useSelector((state) => state.cart);

  // Validation functions
  const validateNumberOnly = (value) => {
    return /^[0-9]*$/.test(value);
  };

  const validateLettersAndSpaces = (value) => {
    return /^[a-zA-Z\s]*$/.test(value);
  };

  const validateNpwpNumber = (value) => {
    if (!value) return "Nomor NPWP tidak boleh kosong";
    if (!validateNumberOnly(value)) return "Nomor NPWP hanya boleh berisi angka";
    if (value.length < 15) return "Nomor NPWP minimal 15 digit";
    if (value.length > 16) return "Nomor NPWP maksimal 16 digit";
    return "";
  };

  const validateCompanyName = (value) => {
    if (!value) return "Nama NPWP tidak boleh kosong";
    if (!validateLettersAndSpaces(value)) return "Nama NPWP hanya boleh berisi huruf dan spasi";
    if (value.length < 3) return "Nama NPWP minimal 3 karakter";
    if (value.length > 100) return "Nama NPWP maksimal 100 karakter";
    return "";
  };

  const validateAddress = (value) => {
    if (!value) return "Alamat NPWP tidak boleh kosong";
    if (value.length < 10) return "Alamat NPWP minimal 10 karakter";
    if (value.length > 500) return "Alamat NPWP maksimal 500 karakter";
    return "";
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Apply validation based on field type
    if (name === "npwp_no") {
      if (value === "" || validateNumberOnly(value)) {
        setFormData({
          ...formData,
          [name]: value,
        });
        // Clear error when input is valid
        setErrors({
          ...errors,
          [name]: validateNpwpNumber(value)
        });
      }
    } else if (name === "npwp_company") {
      if (value === "" || validateLettersAndSpaces(value)) {
        setFormData({
          ...formData,
          [name]: value,
        });
        // Clear error when input is valid
        setErrors({
          ...errors,
          [name]: validateCompanyName(value)
        });
      }
    } else if (name === "npwp_address") {
      setFormData({
        ...formData,
        [name]: value,
      });
      // Clear error when input is valid
      setErrors({
        ...errors,
        [name]: validateAddress(value)
      });
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    // Validate on blur for better UX
    if (name === "npwp_no") {
      setErrors({
        ...errors,
        [name]: validateNpwpNumber(value)
      });
    } else if (name === "npwp_company") {
      setErrors({
        ...errors,
        [name]: validateCompanyName(value)
      });
    } else if (name === "npwp_address") {
      setErrors({
        ...errors,
        [name]: validateAddress(value)
      });
    }
  };

  const handleFileChange = (event) => {
    const imageFile = event.target.files[0];
    setSendFile(imageFile);

    const reader = new FileReader();
    reader.onload = () => {
      setFormData({
        ...formData,
        fileData: reader.result,
      });
    };
    reader.readAsDataURL(imageFile);
  };

  const handlerFakturPajak = (event) => {
    event.preventDefault();

    // Validate all fields before submission
    const npwpNoError = validateNpwpNumber(formData.npwp_no);
    const companyError = validateCompanyName(formData.npwp_company);
    const addressError = validateAddress(formData.npwp_address);

    // Update errors state
    setErrors({
      npwp_no: npwpNoError,
      npwp_company: companyError,
      npwp_address: addressError,
    });

    // Check if there are any validation errors
    if (npwpNoError || companyError || addressError) {
      notifyWarning("Lengkap Formulir Faktur Pajak dengan benar!");
      return;
    }

    if (!sendFile) {
      notifyWarning("Foto NPWP Anda Belum Terisi!");
      return;
    }

    if (sendFile.size > 2 * 1024 * 1024) {
      notifyWarning(
        "Harap periksa ukuran file Anda. Ukuran maksimal yang diperbolehkan adalah 2MB."
      );
      return;
    }

    if (
      sendFile.type !== "image/jpg" &&
      sendFile.type !== "image/png" &&
      sendFile.type !== "image/jpeg"
    ) {
      notifyWarning(
        "Maaf, hanya file dengan ekstensi PNG, JPG, dan JPEG yang diperbolehkan."
      );
      return;
    }

    setButtonDisable(true);
    setLoading(true);

    if (formData) {
      if (status_login) {
        dispatch(sendDocumentNpwp(sendFile));
      } else {
        notifyWarning("Anda belum login!");
      }
    }
  };

  useEffect(() => {
    if (documentNpwp.status === "fulfilled") {
      setButtonDisable(false);
      setLoading(false);
      dispatch(addDocumentNpwp({ formData, label: "Sudah Lengkap" }));
      notifySuccess("Kelengkapan NPWP Berhasil di Upload");
      dispatch(resetStatusNPWP("idle"));
      // Reset form and errors
      setFormData({
        npwp_no: "",
        npwp_company: "",
        npwp_address: "",
      });
      setErrors({
        npwp_no: "",
        npwp_company: "",
        npwp_address: "",
      });
      setSendFile("");
      props.onHide();
    } else if (documentNpwp.status === "error") {
      setButtonDisable(false);
      setLoading(false);
      notifyWarning(`${documentNpwp.error}`);
      dispatch(resetStatusNPWP("idle"));
    } else if (documentNpwp.status === "idle") {
      setButtonDisable(false);
      setLoading(false);
    }
  }, [dispatch, documentNpwp.status, props, documentNpwp.error, formData]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!props.show) {
      setFormData({
        npwp_no: "",
        npwp_company: "",
        npwp_address: "",
      });
      setErrors({
        npwp_no: "",
        npwp_company: "",
        npwp_address: "",
      });
      setSendFile("");
      setButtonDisable(false);
      setLoading(false);
    }
  }, [props.show]);

  return (
    <>
      <Modal show={props.show} onHide={props.onHide} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Faktur Pajak</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handlerFakturPajak}>
            <div className="form">
              <div className="mb-3">
                <Form.Group controlId="npwp_no" className="mb-3">
                  <Form.Label>Nomor NPWP <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="npwp_no"
                    placeholder="Masukkan No NPWP Anda (15-16 digit)"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={errors.npwp_no ? "is-invalid" : ""}
                    value={formData.npwp_no}
                  />
                  {errors.npwp_no && (
                    <div className="invalid-feedback d-block">
                      <span className="text-danger">{errors.npwp_no}</span>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="mb-3">
                <Form.Group controlId="npwp_company" className="mb-3">
                  <Form.Label>Nama Wajib Pajak <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="npwp_company"
                    placeholder="Masukan Nama NPWP (sesuai KTP)"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={errors.npwp_company ? "is-invalid" : ""}
                    value={formData.npwp_company}
                  />
                  {errors.npwp_company && (
                    <div className="invalid-feedback d-block">
                      <span className="text-danger">{errors.npwp_company}</span>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="mb-3">
                <Form.Group controlId="npwp_address" className="mb-3">
                  <Form.Label>Alamat Tempat Tinggal<span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="npwp_address"
                    placeholder="Masukkan Alamat NPWP (sesuai KTP)"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={errors.npwp_address ? "is-invalid" : ""}
                    value={formData.npwp_address}
                  />
                  {errors.npwp_address && (
                    <div className="invalid-feedback d-block">
                      <span className="text-danger">{errors.npwp_address}</span>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="mb-3">
                <Form.Group controlId="foto_npwp" className="mb-3">
                  <Form.Label>Foto NPWP</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="file"
                      className="custom-file"
                      name="foto_npwp"
                      placeholder="Masukkan Foto NPWP Anda"
                      onChange={handleFileChange}
                      style={{
                        border: "2px solid #f8b038",
                        borderRight: "none",
                        borderTopRightRadius: "0",
                        borderBottomRightRadius: "0",
                        backgroundColor: "#fff",
                        fontSize: "14px"
                      }}
                      id="fileInput"
                      accept="image/jpg,image/jpeg,image/png"
                    />
                    <label
                      htmlFor="fileInput"
                      className="btn"
                      style={{
                        backgroundColor: "#f8b038",
                        color: "white",
                        border: "2px solid #f8b038",
                        borderLeft: "none",
                        borderTopLeftRadius: "0",
                        borderBottomLeftRadius: "0",
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "500",
                        minWidth: "120px",
                        cursor: "pointer",
                        marginBottom: "0"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#e69c2e";
                        e.target.style.borderColor = "#e69c2e";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#f8b038";
                        e.target.style.borderColor = "#f8b038";
                      }}
                    >
                      Pilih Gambar
                    </label>
                  </div>
                  <p
                    className="text-secondary mt-2"
                    style={{ fontSize: "12px" }}
                  >
                    <i>
                      *File: Maks. 2MB, tipe yang diperbolehkan: PNG, JPG, JPEG.
                    </i>
                  </p>
                </Form.Group>
              </div>
              <div className="alert alert-info mt-3" role="alert">
                <div className="d-flex align-items-start">
                  <span className="me-2" style={{ fontSize: '18px' }}>⚠️</span>
                  Faktur Pajak akan dibuatkan 3-7 Hari Kerja setelah Invoice Paid.
                </div>
              </div>
            </div>
            <div className="row mt-5">
              <button
                className="btn btn-modal-login"
                disabled={buttonDisable}
                type="submit"
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Simpan"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ModalFakturPajak;
